import { useCallback, useMemo, useState } from "react";

export const PENDING_ACTION_STATUS = Object.freeze({
  PENDING: "pending",
  EXECUTING: "executing",
  COMPLETED: "completed",
  FAILED: "failed"
});

function getFirstPendingAction(response) {
  if (
    response?.actionStatus !== "confirmation_required" ||
    !Array.isArray(response?.pendingActions) ||
    response.pendingActions.length === 0
  ) {
    return null;
  }

  return response.pendingActions[0] || null;
}

function buildCompletedAction(currentAction, response) {
  const executedAction = Array.isArray(response?.executedActions)
    ? response.executedActions[0]
    : null;

  const result =
    executedAction?.result ||
    executedAction ||
    response?.result ||
    null;

  return {
    ...(currentAction || {}),
    result,
    completedAt: new Date().toISOString()
  };
}

function buildFailedAction(currentAction, error) {
  return {
    ...(currentAction || {}),
    error: {
      message:
        error?.message ||
        "The action could not be completed."
    },
    failedAt: new Date().toISOString()
  };
}

export default function usePendingAction() {
  const [pendingAction, setPendingAction] = useState(null);

  const [pendingActionStatus, setPendingActionStatus] =
    useState(PENDING_ACTION_STATUS.PENDING);

  const registerPendingAction = useCallback((response) => {
    const nextAction = getFirstPendingAction(response);

    if (!nextAction) {
      return false;
    }

    setPendingAction(nextAction);
    setPendingActionStatus(
      PENDING_ACTION_STATUS.PENDING
    );

    return true;
  }, []);

  const markPendingActionExecuting = useCallback(() => {
    setPendingActionStatus(
      PENDING_ACTION_STATUS.EXECUTING
    );
  }, []);

  const markPendingActionCompleted = useCallback(
    (response) => {
      setPendingAction((currentAction) =>
        buildCompletedAction(
          currentAction,
          response
        )
      );

      setPendingActionStatus(
        PENDING_ACTION_STATUS.COMPLETED
      );
    },
    []
  );

  const markPendingActionFailed = useCallback(
    (error) => {
      setPendingAction((currentAction) =>
        buildFailedAction(
          currentAction,
          error
        )
      );

      setPendingActionStatus(
        PENDING_ACTION_STATUS.FAILED
      );
    },
    []
  );

  const clearPendingAction = useCallback(() => {
    setPendingAction(null);
    setPendingActionStatus(
      PENDING_ACTION_STATUS.PENDING
    );
  }, []);

  const hasPendingAction = pendingAction !== null;

  const isAwaitingApproval =
    hasPendingAction &&
    pendingActionStatus ===
      PENDING_ACTION_STATUS.PENDING;

  const isExecutingAction =
    hasPendingAction &&
    pendingActionStatus ===
      PENDING_ACTION_STATUS.EXECUTING;

  const isCompletedAction =
    hasPendingAction &&
    pendingActionStatus ===
      PENDING_ACTION_STATUS.COMPLETED;

  const isFailedAction =
    hasPendingAction &&
    pendingActionStatus ===
      PENDING_ACTION_STATUS.FAILED;

  return useMemo(
    () => ({
      pendingAction,
      pendingActionStatus,
      hasPendingAction,
      isAwaitingApproval,
      isExecutingAction,
      isCompletedAction,
      isFailedAction,
      registerPendingAction,
      markPendingActionExecuting,
      markPendingActionCompleted,
      markPendingActionFailed,
      clearPendingAction
    }),
    [
      pendingAction,
      pendingActionStatus,
      hasPendingAction,
      isAwaitingApproval,
      isExecutingAction,
      isCompletedAction,
      isFailedAction,
      registerPendingAction,
      markPendingActionExecuting,
      markPendingActionCompleted,
      markPendingActionFailed,
      clearPendingAction
    ]
  );
}